import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EnvironmentService } from '../../../environments/environment.service';
import { selectAuthLoginState } from '../../core/auth/auth.selectors';
import { LogService } from '../../core/log/log.service';
import { ContentService } from '../../domain/content/content.service';
import { ErrorMessage } from '../../domain/error/error-message.model';
import { selectUserId, selectUserIsActivated } from '../../domain/user/user.selectors';
import { getPermission } from '../util/util';
import {
	checkPermissionDialog,
	closeDialog,
	closeInlineContent,
	errorDialog,
	evaluatePermissionDialog,
	loadDialog,
	loadInlineContent,
	openDialog,
	openInlineContent
} from './dn-dialog.actions';
import { openEmailConfirmDialog } from './mail-confirm-dialog/mail-confirm-dialog.actions';
import { PermissionDialogComponent } from './permission-dialog/permission-dialog.component';
import {
	PermissionDialogData,
	PermissionVisibility,
	PermissionVisualizationType
} from './permission-dialog/permission.model';
import { showSnackbar } from '../../core/notifications/notification.actions';
import { NotificationService } from '../../core/notifications/notification.service';
import { dialogClosedEvent, setCurrentPage } from '../../core/current-page/current-page.actions';
import { awaitAuthSettled } from '../util/operators';

@Injectable({ providedIn: 'root' })
export class DialogEffects {
	// Stack dei nomi dei dialog aperti (ordine LIFO).
	// Necessario per ripristinare currentPage.dialog al parent quando un dialog nested si chiude.
	private dialogStack: string[] = [];

	constructor(
		private actions$: Actions,
		private store: Store,
		private dialogRef: MatDialog,
		private logService: LogService,
		private notificationService: NotificationService,
		private contentService: ContentService,
		private envService: EnvironmentService,
		// eslint-disable-next-line @typescript-eslint/ban-types
		@Inject(PLATFORM_ID) private platformId: Object
	) {}

	loadDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadDialog),
			// new-login (1.3): trattieni finché l'auth è assestata — non decidere un
			// dialog di permesso su uno snapshot REFRESHING. Vedi CU-21.
			awaitAuthSettled(this.store),
			tap((payload) => this.logService.info('Effect: loadDialog ' + JSON.stringify(payload))),
			concatLatestFrom(() => this.store.select(selectUserId)),
			switchMap(([payload, userId]) =>
				this.contentService.loadContentById(userId, payload.contentId, payload.template).pipe(
					tap((response) =>
						this.logService.infoDebug('Effect: loadDialog data received --> ', response)
					),
					concatLatestFrom(() => this.store.select(selectAuthLoginState)),

					map(([response, loginState]) => {
						if (
							getPermission(
								PermissionVisibility.RESTRICTIONS,
								loginState,
								response.abilitato,
								true
							) !== PermissionVisualizationType.PermissionAllowed
						) {
							return openDialog({
								content: undefined,
								urlToDownload: undefined,
								urlWithContext: undefined,
								componentType: PermissionDialogComponent,
								componentName: 'PermissionDialogComponent',
								disableClose: true,
								panelClass: 'dn-dialog-small',
								data: <PermissionDialogData>{
									templateVisibility: PermissionVisibility.RESTRICTIONS,
									callbackUrl: payload.contextUrl + response.permalink,
									showClose: true,
									selectorAbilitato: of(response.abilitato)
								}
							});
						} else {
							return openDialog({
								content: response,
								urlToDownload: this.envService.contentPath + response.url,
								urlWithContext: payload.contextUrl + response.permalink,
								data: payload.dataDialog,
								componentType: payload.componentType,
								componentName: payload.componentName
							});
						}
					}),
					catchError((error: ErrorMessage) => of(errorDialog({ error })))
				)
			)
		)
	);

	loadInlineContent$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loadInlineContent),
			// new-login (1.3): vedi loadDialog$
			awaitAuthSettled(this.store),
			tap((payload) => this.logService.info('Effect: loadInlineContent ' + JSON.stringify(payload))),
			concatLatestFrom(() => this.store.select(selectUserId)),
			switchMap(([payload, userId]) =>
				this.contentService.loadContentById(userId, payload.contentId, payload.template).pipe(
					takeUntil(this.actions$.pipe(ofType(closeInlineContent))),
					tap((response) =>
						this.logService.infoDebug('Effect: loadInlineContent data received --> ', response)
					),
					concatLatestFrom(() => this.store.select(selectAuthLoginState)),
					concatLatestFrom(() => this.store.select(selectUserIsActivated)),
					map(([[response, loginState], isUserActivated]) => {
						const permission = getPermission(
							PermissionVisibility.RESTRICTIONS,
							loginState,
							response.abilitato,
							isUserActivated
						);
						if (permission === PermissionVisualizationType.PermissionAllowed) {
							return openInlineContent({
								content: response,
								urlToDownload: this.envService.contentPath + response.url,
								urlWithContext: payload.contextUrl + response.permalink,
								template: payload.template,
								anchorElementId: payload.anchorElementId
							});
						}
						// UserNotActivated: route to the dedicated mail-confirm dialog
						// to match loadDialog/evaluatePermissionDialog behavior — avoids
						// rendering an empty PermissionDialog branch for unactivated users.
						if (permission === PermissionVisualizationType.UserNotActivated) {
							return openEmailConfirmDialog({
								callbackUrl: payload.contextUrl + response.permalink
							});
						}
						return openDialog({
							content: undefined,
							urlToDownload: undefined,
							urlWithContext: undefined,
							componentType: PermissionDialogComponent,
							componentName: 'PermissionDialogComponent',
							disableClose: true,
							panelClass: 'dn-dialog-small',
							data: <PermissionDialogData>{
								templateVisibility: PermissionVisibility.RESTRICTIONS,
								callbackUrl: payload.contextUrl + response.permalink,
								showClose: true,
								selectorAbilitato: of(response.abilitato)
							}
						});
					}),
					catchError((error: ErrorMessage) => of(errorDialog({ error })))
				)
			)
		)
	);

	errorDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(errorDialog),
			tap((action) => this.logService.info('Effect: errorDialog', action)),
			map((action) => action.error),
			map((error) =>
				showSnackbar({
					payload: this.notificationService.getNotificationFromError(error)
				})
			)
		)
);

	openDialog$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(openDialog),
				// I dialog non devono MAI aprirsi durante l'SSR: il server gira da guest,
				// quindi su un contenuto riservato aprirebbe il PermissionDialog e — se
				// l'app si stabilizza col dialog aperto — lo serializzerebbe nell'HTML,
				// che la cache SSR poi serve a TUTTI. Il client loggato non lo apre
				// (permesso ok) e quindi non lo chiude mai: wall di login permanente per
				// un utente autenticato (bug /video/110577, ago 2026).
				filter(() => isPlatformBrowser(this.platformId)),
				tap((payload) => this.logService.info('Effect: openDialog')),
				awaitAuthSettled(this.store),
				tap(
					({
						content,
						urlToDownload,
						urlWithContext,
						componentType,
						componentName,
						disableClose,
						data,
						panelClass
					}) => {
						const dialogName = componentName;
						const ref = this.dialogRef.open(componentType, {
							closeOnNavigation: true,
							disableClose: disableClose,
							panelClass: panelClass ? panelClass : 'dn-dialog-large',
							data: {
								content: content,
								urlToDownload: urlToDownload,
								urlWithContext: urlWithContext,
								...data
							}
						});
						this.dialogStack.push(dialogName);
						this.store.dispatch(setCurrentPage({ currentPage: { dialog: dialogName } }));
						ref.afterClosed().subscribe(() => {
							this.logService.info('Effect: dialog afterClosed for ' + dialogName);
							const idx = this.dialogStack.lastIndexOf(dialogName);
							if (idx >= 0) {
								this.dialogStack.splice(idx, 1);
							}
							const top = this.dialogStack[this.dialogStack.length - 1];
							this.store.dispatch(setCurrentPage({ currentPage: { dialog: top } }));
							this.store.dispatch(dialogClosedEvent({ componentName: dialogName }));
						});
					}
				)
			),
		{ dispatch: false }
	);

	closeDialog$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(closeDialog),
				tap((action) => this.logService.info('Effect: DialogClosed', action)),
				// Chiude solo il dialog top-most senza mutare l'array openDialogs di MatDialog;
				// afterClosed() (sottoscritto in openDialog$) si occupa di aggiornare lo stack
				// e currentPage.dialog.
				tap(() => this.dialogRef.openDialogs.at(-1)?.close())
			),
		{ dispatch: false }
	);

	checkPermissionDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(checkPermissionDialog),
			// new-login (1.3): vedi loadDialog$
			awaitAuthSettled(this.store),
			tap((action) => this.logService.info('Effect: checkPermissionDialog', action)),
			concatLatestFrom(({ selectorAbilitato: selectorAbilitato }) =>
				selectorAbilitato ? selectorAbilitato : of(undefined)
			),
			concatLatestFrom(() => this.store.select(selectUserIsActivated)),
			concatLatestFrom(() => this.store.select(selectAuthLoginState)),
			filter(
				([
					[[{ templateVisibility: templateVisibility }, isAbilitato], isUserActivated],
					authLoginState
				]) => {
					const permission = getPermission(templateVisibility, authLoginState, isAbilitato, isUserActivated);
					// UserNotActivated is handled exclusively by mail-confirm-dialog.effects.ts
					// (openEmailConfirmAfterLoad$), so skip it here to avoid opening the dialog twice.
					return permission !== PermissionVisualizationType.PermissionAllowed &&
						permission !== PermissionVisualizationType.UserNotActivated;
				}
			),
			map(
				([
					[
						[
							{
								templateVisibility: templateVisibility,
								callbackUrl: callbackUrl,
								selectorAbilitato: selectorAbilitato
							},
							isAbilitato
						],
						isUserActivated
					],
					authLoginState
				]) =>
					openDialog({
						content: undefined,
						urlWithContext: undefined,
						urlToDownload: undefined,
						componentType: PermissionDialogComponent,
						componentName: 'PermissionDialogComponent',
						disableClose: true,
						panelClass: 'dn-dialog-small',
						data: {
							templateVisibility: templateVisibility,
							selectorAbilitato: selectorAbilitato,
							showClose: true,
							callbackUrl: callbackUrl
						}
					})
			)
		)
	);

	evaluatePermissionDialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(evaluatePermissionDialog),
			// new-login (1.3): vedi loadDialog$
			awaitAuthSettled(this.store),
			tap((action) => {
				this.logService.info('Effect: evaluatePermissionDialog ', action.type);
			}),
			concatLatestFrom(({ abilitatoSelector }) => [
				abilitatoSelector ? abilitatoSelector : of(undefined),
				this.store.select(selectUserIsActivated),
				this.store.select(selectAuthLoginState)
			]),
			map(
				([
					{
						templateVisibility: templateVisibility,
						abilitatoSelector: abilitatoSelector,
						callbackUrl: callbackUrl,
						showClose: showClose,
						nextAction: nextAction
					},
					isAbilitato,
					isUserActivated,
					authLoginState
				]) => {
					const permission = getPermission(
						templateVisibility,
						authLoginState,
						isAbilitato,
						isUserActivated
					);
					// PermissionAllowed and UserNotActivated both advance the caller's
					// downstream flow. Activation enforcement is a site-access concern
					// now (handled by the SiteAccess feature's `openGatesOnUserLoad$`
					// on every loadUserSuccess), so the nextAction can flip
					// `containerOperation → OK` immediately. The consumer
					// (dynamic-container-controller) gates its final content-dialog
					// render on `selectSiteAccessCleared ∧ containerOperation === OK`,
					// so an unactivated user still can't see the content — they'll
					// see the mail-confirm dialog until they act on it.
					if (
						permission === PermissionVisualizationType.PermissionAllowed ||
						permission === PermissionVisualizationType.UserNotActivated
					) {
						return nextAction;
					}
					return openDialog({
						content: undefined,
						urlToDownload: undefined,
						urlWithContext: undefined,
						componentType: PermissionDialogComponent,
						componentName: 'PermissionDialogComponent',
						disableClose: true,
						panelClass: 'dn-dialog-small',
						data: {
							templateVisibility: templateVisibility,
							callbackUrl: callbackUrl,
							selectorAbilitato: abilitatoSelector,
							showClose: showClose,
							nextAction
						}
					});
				}
			)
		)
	);
}
